import React from 'react';
import { StaticImage } from 'gatsby-plugin-image';
import useStoryblok from '~/utils/storyblok-service';
import { graphql } from 'gatsby';

import Layout from '~/components/Layout';
import Seo from '~/components/SEO';
import TextCard from '~/components/TextCard';
import IconLink from '~/components/IconLink';
import Star from '~/components/Star';
import Image from '~/components/Dynamic/Image';

import ForCreativesSlider from '~/components/ForCreativesSlider'

import longArrow from '~/images/creatives/long-arrow-black.svg';
import photographers from '~/images/creatives/photographers.jpg';
import videographers from '~/images/creatives/videographers.jpg';
import drone from '~/images/creatives/drone.jpg';
import vr from '~/images/creatives/vr.jpg';

import FeatureItem from '~/components/FeatureItem';

const create = [
  {
    image: photographers,
    heading: 'Photographers',
    content: 'Most of our creatives wield a camera to make magic for major clients.',
  },
  {
    image: videographers,
    heading: 'Videographers',
    content: 'Some clients need Hollywood quality video to bring their visions to life.',
  },
  {
    image: drone,
    heading: 'Drone Pilots',
    content: 'We hire expert drone pilots to take their art to the skies for stunning shots.',
  },
  {
    image: vr,
    heading: 'Virtual Tour Agents',
    content: 'Know how to tell the story of a specific location? We’ve got work for you.',
  },
];

function Creatives({ data, location, pageContext }) {
  const story = useStoryblok(data.storyblokEntry, location);
  const content = story.content;

  const {
    hero,
    createFeatures,
    createHeading,
    creativesGrid,
    shootFeatures,
    shootHeading,
    steps,
    stepsHeading,
    workingWithBoom,
  } = content;

  return (
    <Layout
      footerCTAContent={{
        heading: 'Come for the work, leave behind the hassle',
        content: 'Working with BOOM takes the paperwork out of photography. Do what you do best.',
        buttons: [{ title: 'Join us', url: '#form' }],
      }}
      locale={pageContext.locale}
    >
      <Seo {...content.seo} lang={story.lang} pageContext={pageContext} />
      <section className={`mt-16 lg:mt-36 overflow-hidden`}>
        <div className="container">
          <div className="flex flex-wrap">
            <div className="relative z-10 w-full md:w-1/2 xl:w-5/12">
              <TextCard {...hero[0]?.textCard[0]} />
            </div>
            <div className="flex flex-col items-end justify-end flex-auto w-full md:w-1/2 xl:w-7/12">
              <div className="flex-auto w-full mt-12 -mb-12 md:ml-auto lg:mr-auto xl:mt-0">
                <Image blok={hero[0]?.Asset[0]?.image} imageClassName="w-full md:pl-8 lg:pl-0" />
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className={`bg-beige py-16 lg:py-28`}>
        <div className="container">
          <div className="flex flex-col items-center justify-center mb-12 lg:mb-24">
            <TextCard
              className={`items-center max-w-3xl`}
              headingClass={`text-center`}
              heading={shootHeading[0]?.heading}
              contentClass={`text-center max-w-3xl`}
              content={shootHeading[0]?.content}
            />

            <IconLink color={'black'} to={shootHeading[0]?.button[0]?.button?.url} className={`text-20px mt-8`}>
              {shootHeading[0]?.button[0]?.title}
            </IconLink>
          </div>
          <div className="flex flex-wrap justify-around flex-auto -ml-12">
            {shootFeatures.map((shootFeat, i) => (
              <div
                key={`shootFeat${i}`}
                className={`flex justify-around pl-12  ${i === shootFeatures.length - 1 ? 'mb-0' : 'mb-12 xl:mb-0'}`}
              >
                <FeatureItem
                  className={`items-start md:max-w-md lg:max-w-xs ${shootFeat.className}`}
                  iconSrc={shootFeat.icon.filename}
                  heading={shootFeat.heading}
                  imgHeight={`h-[118px]`}
                  headingClass={shootFeat.headingClass}
                  content={shootFeat.content}
                  contentClass={`text-17px `}
                  iconClass={shootFeat.iconClass}
                />
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className={`bg-black mb-20 md:mb-28`}>
        <div className="container py-16 lg:py-28">
          <div className="flex flex-wrap text-white border border-collapse border-border-grey ">
            <div className="flex flex-col w-full lg:w-6/12">
              <div className="flex-auto border border-border-grey ">
                <div className="p-12 ">
                  <div className="text-24px font-heading leading-[1.39em]">
                    {creativesGrid[0]?.gridTopUpperLeft[0].content}
                  </div>

                  <div className="mt-20 text-lime uppercase font-heading text-13px font-extrabold tracking-2 leading-[1.4em]">
                    {creativesGrid[0]?.gridTopUpperLeft[0].name}
                  </div>
                  <div className="italic text-12px">
                    <p>{creativesGrid[0]?.gridTopUpperLeft[0].role}</p>{' '}
                  </div>
                </div>
              </div>
              <div className="flex-auto border border-border-grey ">
                <div className="p-12 ">
                  <div className="text-24px font-heading leading-[1.39em]">
                    {creativesGrid[0]?.gridTopLowerLeft[0].content}
                  </div>

                  <div className="mt-20 text-lime uppercase font-heading text-13px font-extrabold tracking-2 leading-[1.4em]">
                    {creativesGrid[0]?.gridTopLowerLeft[0].name}
                  </div>
                  <div className="italic text-12px">
                    <p>{creativesGrid[0]?.gridTopLowerLeft[0].role}</p>{' '}
                  </div>
                </div>
              </div>
            </div>
            <div className="relative w-full lg:w-6/12 ">
              <div className="absolute top-[7%] right-[16%] z-10">
                <Star className="text-black transform rotate-45" />
              </div>
              <div className="absolute bottom-[10%] right-[24%] lg:bottom-[28%] lg:right-[15%] z-10 text-black">
                <Star />
              </div>
              <div className="hidden lg:block absolute bottom-[10%] left-[10%] z-10 text-black">
                <Star />
              </div>

              <div className="absolute inset-0 z-0">
                <StaticImage
                  src={`../assets/images/creatives/grid-bg-bigblank.jpg`}
                  className={'object-cover w-full h-full'}
                  alt=""
                />
              </div>
              <div className="relative z-10 p-12 lg:max-w-md">
                <div className="font-semibold leading-none text-36px md:text-72px font-heading tracking-2">
                  <span className="block w-full h-16 text-black text-122px">“</span>
                  {creativesGrid[0]?.gridTopRight}
                </div>
              </div>
            </div>
          </div>
          <div className="flex flex-wrap text-white border border-collapse border-border-grey mt-[-2px]">
            <div className="relative order-last w-full border border-collapse border-border-grey md:w-1/2 lg:w-1/3 md:order-1 md:flex h-80 md:h-auto">
              <Image blok={creativesGrid[0]?.gridBottomLeft} className="w-full h-full" imageClassName="object-cover w-full h-full inset-0 absolute" />
            </div>
            <div className="order-2 w-full border border-collapse border-border-grey md:w-1/2 lg:w-1/3">
              <div className="p-12">
                <div className="text-24px font-heading leading-[1.39em]">
                  {creativesGrid[0]?.gridBottomMiddle[0]?.content}
                </div>

                <div className="mt-20 text-lime uppercase font-heading text-13px font-extrabold tracking-2 leading-[1.4em]">
                  {creativesGrid[0]?.gridBottomMiddle[0]?.name}
                </div>
                <div className="italic text-12px">
                  <p> {creativesGrid[0]?.gridBottomMiddle[0]?.role}</p>{' '}
                </div>
              </div>
            </div>
            <div className="order-3 w-full border border-collapse border-border-grey lg:w-1/3">
              <div className="p-12 ">
                <div className="text-24px font-heading leading-[1.39em]">
                  {creativesGrid[0]?.gridBottomRight[0]?.content}
                </div>

                <div className="mt-20 text-lime uppercase font-heading text-13px font-extrabold tracking-2 leading-[1.4em]">
                  {creativesGrid[0]?.gridBottomRight[0]?.name}
                </div>
                <div className="italic text-12px">
                  <p>{creativesGrid[0]?.gridBottomRight[0]?.role}</p>{' '}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="my-20 md:my-36">
        <div className="container">
          <div className="flex text-center md:text-left">
            <TextCard heading={createHeading} />
          </div>
          <div className="mt-16">
            <div className="flex flex-wrap -ml-16">
              {createFeatures.map((createItem, i) => (
                <div
                  key={`createItem${i}`}
                  className={`w-full mb-12 lg:mb-0 md:w-1/2 pl-16 lg:w-1/4 ${
                    i >= createFeatures.length - 2 && 'md:mb-12 last:mb-0 lg:mb-0'
                  }`}
                >
                  <div className="flex flex-col text-center md:text-left">
                    <div>
                      <Image blok={createItem.icon} className={`object-contain object-center-left`} imageClassName="max-h-[62px] mr-auto"  />
                    </div>
                    <div className="mt-6">
                      <h4 className="text-h4">{createItem.heading}</h4>
                    </div>
                    <div className="mt-4 text-17px ">
                      <p>{createItem.content}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {content.workingWithBoom?.length && (
       <ForCreativesSlider content={content.workingWithBoom} />
      )}

      {/* <section className="my-14 md:my-28 bg-beige">
        <div className="container py-28">
          <div className="flex flex-col">
            <div className="flex justify-start mb-12 lg:mb-24">
              <div className="flex flex-col">
                {content.workingWithBoom?.length > 0 && <DynamicComponent blok={content.workingWithBoom[0]} />}
              </div>
            </div>
            <div className="flex flex-wrap items-center -ml-16">
              <div className="w-full pl-16 lg:w-2/3">
                <StaticImage src={`../assets/images/creatives/working-with-boom.png`} alt="" />
              </div>
              <div className="w-full pl-16 mt-12 lg:w-1/3 lg:mt-0">
                {boomWork.map((work, i) => (
                  <div key={`boomWork${i}`} className="flex flex-col max-w-[24rem] py-3 mb-1">
                    <div className="py-2 pl-5 border-l-2 border-blue">
                      <div className="flex flex-col">
                        <h4 className="mb-1 font-extrabold uppercase text-20px u-h4">{work.title}</h4>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section> */}

      <section className="my-20 md:my-28">
        <div className="container">
          <div className="flex justify-center mb-12 lg:mb-24">
            <TextCard
              className={`items-center max-w-3xl`}
              headingClass={`text-center`}
              heading={stepsHeading[0]?.heading}
              contentClass={` text-center max-w-2xl prose-lg md:prose`}
              content={stepsHeading[0]?.content}
            />
          </div>
          <div className="flex flex-wrap justify-center -mt-8 lg:flex-nowrap md:mt-0">
            {steps.map((step, i, arr) => (
              <>
                <div className={`w-full lg:w-3/12 mx-6 mt-8 lg:mt-0`}>
                  <div className="flex flex-col items-center text-center">
                    <div className="w-full">
                      <Image
                        blok={step.icon}
                        className="object-contain"
                        imageClassName="object-contain  max-h-16 mr-auto "
                      />
                    </div>
                    <div className="mt-6">
                      <h4 className="text-h4">{step.heading}</h4>
                    </div>
                    <div className="mt-4 text-17px ">
                      <p>{step.content}</p>
                    </div>
                  </div>
                </div>
                {i !== arr.length - 1 && (
                  <div className="items-center w-16 py-10 mt-6 transform rotate-90 lg:py-0 lg:mt-0 lg:rotate-0 lg:flex">
                    <img src={longArrow} alt="" />
                  </div>
                )}
              </>
            ))}
          </div>
        </div>
      </section>
    </Layout>
  );
}

export default Creatives;

export const query = graphql`
  query forCreativesQuery($id: String!) {
    storyblokEntry(id: { eq: $id }) {
      id
      name
      content
      lang
      slug
      uuid
      full_slug
    }
  }
`;
